import AuthGuard from 'common/guards/AuthGuard'
import FeatureGuard from 'common/guards/FeatureGuard'
// guards
import GuestGuard from 'common/guards/GuestGuard'
import PolicyConsentGuard from 'common/guards/PolicyConsentGuard'
// layouts
import DashboardLayout from 'layouts/dashboard'
import ComparisonPage from 'modules/comparisonPond/pages/comparisonMainPage'
import Cost from 'modules/cost/components/Cost'
import DeadShrimp from 'modules/deadShrimp/components/DeadShrimp'
import Food from 'modules/food/components/Food'
import GrowthShrimp from 'modules/growth/components/GrowthShrimp'
import CategoryPage from 'modules/inventory/pages/category/CategoryPage'
import ProductDetail from 'modules/inventory/pages/product/ProductDetail'
import ProductHistory from 'modules/inventory/pages/product/ProductHistory'
import ProductPage from 'modules/inventory/pages/product/ProductPage'
import InventoryDisplaySettingsPage from 'modules/inventory/pages/settings/InventoryDisplaySettingsPage'
import InventorySettingsPage from 'modules/inventory/pages/settings/InventorySettingsPage'
import InventoryStockEnforcementPage from 'modules/inventory/pages/settings/InventoryStockEnforcementPage'
import StockPage from 'modules/inventory/pages/stock/StockPage'
import WarehousePage from 'modules/inventory/pages/warehouse/WarehousePage'
import GuestLayout from 'modules/landing/components/GuestLayout'
import ShrimpPricePage from 'modules/landing/pages/ShrimpPricePage'
import MedicalSupply from 'modules/medicalSupply/components/MedicalSupply'
import ManagementPage from 'modules/member/pages/ManagementPage'
import NotificationPage from 'modules/notification/pages/NotificationPage'
import NotificationSettingPage from 'modules/notification/pages/NotificationSettingPage'
import OverviewDashboard from 'modules/pond/components/OverviewDashboard'
import PondClearStateWrapper from 'modules/pond/components/PondClearStateWrapper'
import { PondTab } from 'modules/pond/constants'
import SettingPage from 'modules/pond/pages/SettingPage'
import ShrimpSales from 'modules/shrimpSales/components/ShrimpSales'
import ShrimpTopup from 'modules/shrimpTopup/components/ShrimpTopup'
import ShrimpTransfer from 'modules/shrimpTransfer/components/ShrimpTransfer'
import { useUserInfo } from 'modules/user/api/queries'
import { useAllowPondTabs, useFirstAllowedDashboardPath } from 'modules/user/api/selector'
import ChangePhonePage from 'modules/user/pages/ChangePhonePage'
import FeedbackPage from 'modules/user/pages/FeedbackPage'
import ProfilePage from 'modules/user/pages/ProfilePage'
import Water from 'modules/water/components/Water'
import { ElementType, Suspense, lazy } from 'react'
import { useRoutes, useLocation, Navigate } from 'react-router-dom'

// components
import LoadingScreen from 'components/LoadingScreen'

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  const { pathname } = useLocation()
  const isDashboard = pathname.includes('/dashboard')

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

// DOF: waits for permissions before deciding where to send the user - a
// <Navigate> commits its redirect on the first render, so redirecting while
// useUserInfo is still loading would always land on the no-permission
// fallback path instead of the section the user actually has access to.
function DashboardHomeRedirect() {
  const { isLoading: isUserInfoLoading } = useUserInfo()
  const firstAllowedDashboardPath = useFirstAllowedDashboardPath()

  if (isUserInfoLoading) {
    return <LoadingScreen />
  }

  return <Navigate to={firstAllowedDashboardPath} replace />
}

export default function Router() {
  const allowTabs = useAllowPondTabs()
  const getPondTab = () => {
    if (allowTabs.length === 0) return 'dashboard'
    const firstAllowTab = allowTabs[0]
    switch (firstAllowTab) {
      case PondTab.Dashboard:
        return 'dashboard'
      case PondTab.Food:
        return 'food'
      case PondTab.Growth:
        return 'growth'
      case PondTab.Water:
        return 'water'
      case PondTab.DeadShrimp:
        return 'dead-shrimp'
      case PondTab.Medicine:
        return 'medicine'
      case PondTab.Cost:
        return 'cost'
      case PondTab.Sales:
        return 'sales'
      case PondTab.Transfer:
        return 'transfer'
      case PondTab.Topup:
        return 'topup'
      case PondTab.Setting:
        return 'setting'
      default:
        return 'dashboard'
    }
  }

  return useRoutes([
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardHomeRedirect />
        </AuthGuard>
      ),
    },
    {
      path: 'policy-consent',
      element: (
        <AuthGuard>
          <PolicyConsentPage />
        </AuthGuard>
      ),
    },
    {
      path: 'settings',
      element: (
        <AuthGuard>
          <PolicyConsentGuard>
            <DashboardLayout />
          </PolicyConsentGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: '',
          element: <SettingsMainPage />,
        },
      ],
    },
    {
      path: '/shrimp-price',
      element: (
        <GuestLayout>
          <ShrimpPricePage />
        </GuestLayout>
      ),
    },
    // Authentication
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ForgetPassword />
            </GuestGuard>
          ),
        },
      ],
    },
    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <PolicyConsentGuard>
            <DashboardLayout />
          </PolicyConsentGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'farms',
          element: <AllFarmsPage />,
        },
        {
          path: 'farms/:farmId',
          element: <AllPondsPage />,
        },
        {
          path: 'farms/:farmId/pond/:pondId',
          children: [
            {
              element: (
                <PondClearStateWrapper>
                  <PondDetailPage />
                </PondClearStateWrapper>
              ),
              children: [
                { path: '', element: <Navigate to={getPondTab()} replace /> },
                { path: 'dashboard', element: <OverviewDashboard /> },
                { path: 'setting', element: <SettingPage /> },
                { path: 'food', element: <Food /> },
                { path: 'growth', element: <GrowthShrimp /> },
                { path: 'water', element: <Water /> },
                { path: 'dead-shrimp', element: <DeadShrimp /> },
                { path: 'medicine', element: <MedicalSupply /> },
                { path: 'cost', element: <Cost /> },
                { path: 'sales', element: <ShrimpSales /> },
                { path: 'transfer', element: <ShrimpTransfer /> },
                { path: 'topup', element: <ShrimpTopup /> },
              ],
            },
          ],
        },
        {
          path: 'comparison',
          element: (
            <PondClearStateWrapper>
              <ComparisonPage />
            </PondClearStateWrapper>
          ),
        },
        {
          path: 'organization',
          element: <OrganizationOverviewPage />,
        },
        {
          path: 'members',
          element: <ManagementPage />,
        },
        {
          path: 'members/roles',
          element: <ManagementPage />,
        },
        {
          path: 'profile',
          element: (
            <AuthGuard>
              <ProfilePage />
            </AuthGuard>
          ),
        },
        {
          path: 'settings',
          element: (
            <AuthGuard>
              <AppSettingsPage />
            </AuthGuard>
          ),
        },
        {
          path: 'feedback',
          element: (
            <AuthGuard>
              <FeedbackPage />
            </AuthGuard>
          ),
        },
        {
          path: 'change-phone',
          element: (
            <AuthGuard>
              <ChangePhonePage />
            </AuthGuard>
          ),
        },
        {
          path: 'shrimp-price',
          element: (
            <AuthGuard>
              <ShrimpPricePage />
            </AuthGuard>
          ),
        },
        {
          path: 'category',
          element: (
            <AuthGuard>
              <CategoryPage />
            </AuthGuard>
          ),
        },
        {
          path: 'product',
          element: (
            <AuthGuard>
              <ProductPage />
            </AuthGuard>
          ),
        },
        {
          path: 'product',
          children: [
            {
              path: ':id',
              element: (
                <AuthGuard>
                  <ProductDetail />
                </AuthGuard>
              ),
            },
            {
              path: ':id/history',
              element: (
                <AuthGuard>
                  <ProductHistory />
                </AuthGuard>
              ),
            },
          ],
        },
        {
          path: 'warehouse',
          element: (
            <AuthGuard>
              <WarehousePage />
            </AuthGuard>
          ),
        },
        {
          path: 'stock',
          element: (
            <AuthGuard>
              <StockPage />
            </AuthGuard>
          ),
        },
        {
          path: 'inventory',
          children: [
            {
              path: 'settings',
              element: (
                <AuthGuard>
                  <InventorySettingsPage />
                </AuthGuard>
              ),
            },
            {
              path: 'settings/display/:featureCode',
              element: (
                <AuthGuard>
                  <InventoryDisplaySettingsPage />
                </AuthGuard>
              ),
            },
            {
              path: 'settings/stock-enforcement',
              element: (
                <AuthGuard>
                  <InventoryStockEnforcementPage />
                </AuthGuard>
              ),
            },
          ],
        },
        {
          path: 'notifications',
          element: (
            <AuthGuard>
              <NotificationPage />
            </AuthGuard>
          ),
        },
        {
          path: 'policy',
          element: (
            <AuthGuard>
              <PolicyDocumentPage />
            </AuthGuard>
          ),
        },
        {
          path: 'wallet',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="CREDIT_WALLET" permissionKey="credit">
                <WalletPage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="IOT_DEVICE" permissionKey="iot">
                <IotPage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot/new',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="IOT_DEVICE" permissionKey="iot">
                <IotCreatePage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot/:deviceId',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="IOT_DEVICE" permissionKey="iot">
                <IotDetailPage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot/:deviceId/setting',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="IOT_DEVICE" permissionKey="iot">
                <IotSettingPage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot/:deviceId/threshold',
          element: (
            <AuthGuard>
              <FeatureGuard featureKey="IOT_DEVICE" permissionKey="iot">
                <IotThresholdPage />
              </FeatureGuard>
            </AuthGuard>
          ),
        },
        {
          path: 'iot-device',
          element: (
            <AuthGuard>
              <IotDevicePage />
            </AuthGuard>
          ),
        },
        {
          path: 'notifications',
          children: [
            {
              path: 'settings',
              element: (
                <AuthGuard>
                  <NotificationSettingPage />
                </AuthGuard>
              ),
            },
          ],
        },
      ],
    },
  ])
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('modules/auth/pages/Login')))
const Register = Loadable(lazy(() => import('modules/auth/pages/Register')))
const ForgetPassword = Loadable(lazy(() => import('modules/auth/pages/ForgetPassword')))
// // Dashboard
const AllFarmsPage = Loadable(lazy(() => import('modules/farm/pages/AllFarmsPage')))
const AllPondsPage = Loadable(lazy(() => import('modules/pond/pages/AllPondsPage')))
// pond
const PondDetailPage = Loadable(lazy(() => import('modules/pond/pages/PondDetailPage')))
const MemberPage = Loadable(lazy(() => import('modules/member/pages/MemberPage')))
const AccessPage = Loadable(lazy(() => import('modules/member/pages/AccessPage')))
// General
const SettingsMainPage = Loadable(lazy(() => import('modules/settings/pages/SettingsMainPage')))
const AppSettingsPage = Loadable(lazy(() => import('modules/settings/pages/AppSettingsPage')))
// policy (terms & privacy)
const PolicyConsentPage = Loadable(lazy(() => import('modules/policy/pages/PolicyConsentPage')))
const PolicyDocumentPage = Loadable(lazy(() => import('modules/policy/pages/PolicyDocumentPage')))
// wallet
const WalletPage = Loadable(lazy(() => import('modules/wallets/pages/WalletPage')))
// organization
const OrganizationOverviewPage = Loadable(
  lazy(() => import('modules/organization/pages/OrganizationOverviewPage'))
)
// iot
const IotDevicePage = Loadable(lazy(() => import('modules/iot-device/IotDevicePage')))
const IotPage = Loadable(lazy(() => import('modules/iot/pages/IotPage')))
const IotCreatePage = Loadable(lazy(() => import('modules/iot/pages/IotCreatePage')))
const IotDetailPage = Loadable(lazy(() => import('modules/iot/pages/IotDetailPage')))
const IotSettingPage = Loadable(lazy(() => import('modules/iot/pages/IotSettingPage')))
const IotThresholdPage = Loadable(lazy(() => import('modules/iot/pages/IotThresholdPage')))
